import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { ErrorMessage, FieldArray, useFormikContext } from 'formik'
import { IFormikUserModel } from '../typings'
import { ISalesTeam, SalesTeam } from '@user/store/salesTeam/typings'
import { getSalesTeamPositions } from './typings'
import SalesTeamSelect from '../SalesTeamSelect/SalesTeamSelect'
import Box from '@mui/material/Box'
import SalesTeamAssignmentCard from './SalesTeamAssignmentCard'
import FormHelperText from '@mui/material/FormHelperText'
import SectionTitle from '../SectionTitle'

interface Props {
  formVariant: 'mapped' | 'unmapped' | undefined
  salesTeams: ISalesTeam[]
  salesTeamPositionIds: number[] | undefined
  salesTeamResourcesCount: number[] | undefined
}

export const SalesTeamAssignmentsNew: FC<Props> = (props: Props) => {
  const { salesTeams, salesTeamPositionIds, salesTeamResourcesCount } = props
  const [tempSalesTeams, setTempSalesTeams] = useState<ISalesTeam[] | []>([])
  const {
    setFieldValue,
    values: { isActive, salesTeamAssignments },
    errors,
    touched,
  } = useFormikContext<IFormikUserModel>()

  // ========== STATE ==========
  const [addSalesTeamButtonDisabled, setAddSalesTeamButtonDisabled] =
    useState<boolean>(false)

  // ========== H O O K S ==========
  useEffect(() => {
    if (salesTeams?.length > 0) {
      getSalesTeamPositions(salesTeamPositionIds, salesTeams, setFieldValue)
    }
  }, [salesTeams, salesTeamPositionIds, setFieldValue])

  useEffect(() => {
    // prevent user from adding another sales team before sales team
    //selected or if all sales teams already selected
    const disabled =
      salesTeamAssignments &&
      (salesTeamAssignments.length >= salesTeams.length ||
        salesTeamAssignments.findIndex((x) => x.salesTeamId.length == 0) !== -1)
    setAddSalesTeamButtonDisabled(disabled)
  }, [salesTeams.length, salesTeamAssignments])

  const noSalesTeams = React.useMemo(
    () => !salesTeams || salesTeams?.length == 0,
    [salesTeams],
  )

  const filteredSelectedSalesTeams = React.useMemo(() => {
    return salesTeams?.filter((option) => {
      const selected = salesTeamAssignments.find(
        (x) => Number(x.salesTeamId) == option?.id,
      )

      return !selected
    })
  }, [salesTeams, salesTeamAssignments])

  return (
    <Grid container>
      <>
        <FieldArray
          name="salesTeamAssignments"
          render={(arrayHelpers) => (
            <Box width={'100%'}>
              <SectionTitle
                title="Sales Team"
                linkTitle="Manage Sales Teams"
                link="/settings/salesTeams"
              />

              <Typography component="div" mb={0.4} variant="body1">
                Select a Sales Team for this employee
              </Typography>
              <Box
                sx={{
                  backgroundColor: noSalesTeams ? '#f5f5f5' : '',
                }}
              >
                <SalesTeamSelect
                  items={filteredSelectedSalesTeams}
                  onChange={(value) => {
                    setTempSalesTeams(value)
                  }}
                  value={tempSalesTeams}
                  label="Sales Team"
                  disabled={
                    noSalesTeams || addSalesTeamButtonDisabled || !isActive
                  }
                  error={
                    touched.salesTeamAssignments &&
                    Boolean(errors.salesTeamAssignments)
                  }
                />
                {noSalesTeams ? (
                  <FormHelperText
                    sx={{
                      mx: 1.4,
                      py: 0.5,
                      color: (theme) => theme.palette.text.secondary,
                    }}
                  >
                    No Sales Teams Created
                  </FormHelperText>
                ) : null}
              </Box>
              <Box>
                {touched.salesTeamAssignments && errors.salesTeamAssignments ? (
                  <FormHelperText sx={{ mx: 1.4 }} error>
                    <ErrorMessage name="salesTeamAssignments"></ErrorMessage>
                  </FormHelperText>
                ) : null}
              </Box>
              <Button
                variant="outlined"
                sx={{ width: '30%', mt: 1.4 }}
                size="medium"
                disabled={
                  tempSalesTeams?.length == 0 ||
                  noSalesTeams ||
                  addSalesTeamButtonDisabled ||
                  !isActive
                }
                onClick={() => {
                  tempSalesTeams
                    ?.sort((a: SalesTeam, b: SalesTeam) => {
                      return a.name?.toLowerCase() > b.name?.toLowerCase()
                        ? -1
                        : 1
                    })
                    ?.map((row: SalesTeam) => {
                      arrayHelpers.insert(0, {
                        salesTeamId: row?.id,
                        salesTeamPositionId: '',
                      })
                    })

                  setTempSalesTeams([])
                }}
              >
                Add
              </Button>
              <Grid item container gap={2} mt={2.4}>
                {salesTeamAssignments ? (
                  salesTeamAssignments.map((_assignment, index) => (
                    <SalesTeamAssignmentCard
                      key={index}
                      index={index}
                      onDeleteClick={() => arrayHelpers.remove(index)}
                      salesTeams={salesTeams}
                      salesTeamResourcesCountIds={salesTeamResourcesCount}
                    />
                  ))
                ) : (
                  <Typography component="div" mb={0.4} variant="body1">
                    No Sales Team Assignments
                  </Typography>
                )}
              </Grid>
            </Box>
          )}
        />
      </>
    </Grid>
  )
}
